import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { portalApi, type PortalRecentCompletedBookingDto } from '@/api/portal'
import { AlertTriangle, Clock, CalendarDays, Receipt, CheckCircle2, Star } from 'lucide-react'
import FeedbackModal from '../bookings/FeedbackModal'
import { guardPreview } from '@/lib/previewMode'
import { usePortalMoney } from '@/hooks/useMoney'
import { usePortalDates } from '@/hooks/useDates'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'

function StatCard({
  label, value, sub, accent, onClick,
}: {
  label: string
  value: React.ReactNode
  sub?: string
  accent?: 'red' | 'amber' | 'blue' | 'gray'
  onClick?: () => void
}) {
  const colours = {
    red:   'border-red-200 bg-red-50',
    amber: 'border-amber-200 bg-amber-50',
    blue:  'border-blue-200 bg-blue-50',
    gray:  'border-gray-200 bg-white',
  }
  const valColours = {
    red: 'text-red-700', amber: 'text-amber-700', blue: 'text-blue-700', gray: 'text-gray-900',
  }
  const cls = `rounded-xl border p-5 flex flex-col gap-1 ${colours[accent ?? 'gray']} ${onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`
  return (
    <div className={cls} onClick={onClick}>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className={`text-3xl font-bold tabular-nums ${valColours[accent ?? 'gray']}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

function AlertBadge({ status }: { status: string }) {
  if (status === 'Overdue')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700"><AlertTriangle size={11} />Overdue</span>
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><Clock size={11} />Due soon</span>
}

function BookingStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Booked:    'bg-blue-100 text-blue-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-600',
    Rejected:  'bg-amber-100 text-amber-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  )
}

export default function OverviewPage() {
  const { formatDate } = usePortalDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const { fmt: fmtMoney } = usePortalMoney()
  const navigate = useNavigate()
  const [feedbackBooking, setFeedbackBooking] = useState<PortalRecentCompletedBookingDto | null>(null)
  const permissions = usePortalPermissions()

  const { data, isLoading } = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: () => portalApi.getDashboard().then(r => r.data),
  })

  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (!data) return null

  return (
    <div className="space-y-8">
      {feedbackBooking && (
        <FeedbackModal
          booking={feedbackBooking}
          onClose={() => setFeedbackBooking(null)}
        />
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
        <p className="text-sm text-gray-500 mt-1">A summary of your team's training and upcoming activity</p>
      </div>

      {/* Stat cards */}
      <div className={`grid grid-cols-2 gap-4 ${permissions.invoices ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <StatCard
          label="Overdue certifications"
          value={data.overdueCount}
          accent={data.overdueCount > 0 ? 'red' : 'gray'}
          onClick={() => navigate('/portal/compliance')}
        />
        <StatCard
          label="Expiring within 30 days"
          value={data.expiringSoonCount}
          accent={data.expiringSoonCount > 0 ? 'amber' : 'gray'}
          onClick={() => navigate('/portal/compliance')}
        />
        <StatCard
          label="Upcoming bookings"
          value={data.upcomingBookingsCount}
          accent="blue"
          onClick={() => navigate('/portal/bookings')}
        />
        {permissions.invoices && (
          <StatCard
            label="Outstanding invoices"
            value={fmtMoney(data.outstandingInvoiceAmount)}
            accent={data.outstandingInvoiceAmount > 0 ? 'amber' : 'gray'}
            onClick={() => navigate('/portal/invoices')}
          />
        )}
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3">Training alerts</h2>

          {/* Mobile: card list */}
          <div className="sm:hidden bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm divide-y divide-gray-100">
            {data.alerts.map((a, i) => (
              <div
                key={i}
                className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => navigate(`/portal/team/${a.staffMemberId}`)}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-gray-900 text-sm">{a.staffName}</p>
                  <AlertBadge status={a.status} />
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{a.courseName}</p>
                {a.expiryDate && <p className="text-xs text-gray-400 mt-0.5">{fmtDate(a.expiryDate)}</p>}
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Staff member</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Expiry</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.alerts.map((a, i) => (
                  <tr
                    key={i}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/portal/team/${a.staffMemberId}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">{a.staffName}</td>
                    <td className="px-4 py-3 text-gray-600">{a.courseName}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {a.expiryDate ? fmtDate(a.expiryDate) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <AlertBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.overdueCount + data.expiringSoonCount > 20 && (
            <p className="text-xs text-gray-400 mt-2 text-right">
              Showing 20 of {data.overdueCount + data.expiringSoonCount} alerts —{' '}
              <button className="underline hover:text-gray-600" onClick={() => navigate('/portal/compliance')}>view all</button>
            </p>
          )}
        </section>
      )}

      {/* Upcoming bookings */}
      {data.upcomingBookings.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CalendarDays size={16} className="text-blue-500" />
            Upcoming bookings
          </h2>
          <div className="space-y-2">
            {data.upcomingBookings.map(b => (
              <div
                key={b.id}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between hover:shadow-sm cursor-pointer transition-shadow"
                onClick={() => navigate('/portal/bookings')}
              >
                <div>
                  <p className="font-medium text-gray-900">{b.courseName}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmtDate(b.bookingDate)} · {b.places} {b.places === 1 ? 'place' : 'places'}
                  </p>
                </div>
                <BookingStatusBadge status={b.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent completed bookings */}
      {data.recentCompletedBookings.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-green-500" />
            Recent completed training
          </h2>
          <div className="space-y-2">
            {data.recentCompletedBookings.map(b => (
              <div
                key={b.id}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{b.courseName}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmtDate(b.bookingDate)} · {b.places} {b.places === 1 ? 'place' : 'places'}
                  </p>
                </div>
                {permissions.feedback && (
                  <button
                    onClick={() => { if (!guardPreview()) setFeedbackBooking(b) }}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap rounded-[var(--brand-radius-sm)] px-3 py-1.5 border transition-colors cursor-pointer shrink-0 ${
                      b.hasFeedback
                        ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                        : 'border-[var(--brand-secondary)]/30 bg-[var(--brand-secondary)]/10 text-[var(--brand-secondary)] hover:bg-[var(--brand-secondary)]/20'
                    }`}
                  >
                    <Star size={13} className={b.hasFeedback ? 'fill-amber-400 text-amber-400' : ''} />
                    {b.hasFeedback ? `Rated ${b.feedbackRating}/10` : 'Leave feedback'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {data.alerts.length === 0 && data.upcomingBookings.length === 0 && data.recentCompletedBookings.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Receipt size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Everything looks good — no alerts or upcoming bookings.</p>
        </div>
      )}
    </div>
  )
}
